/**
 * Modal for generating a filtered audit report. Submits an async job and closes immediately. The
 * parent is responsible for showing a success toast and polling for completion.
 */

import Alert from "@cloudscape-design/components/alert"
import Box from "@cloudscape-design/components/box"
import Button from "@cloudscape-design/components/button"
import Checkbox from "@cloudscape-design/components/checkbox"
import DatePicker from "@cloudscape-design/components/date-picker"
import FormField from "@cloudscape-design/components/form-field"
import Modal from "@cloudscape-design/components/modal"
import Multiselect, { type MultiselectProps } from "@cloudscape-design/components/multiselect"
import RadioGroup from "@cloudscape-design/components/radio-group"
import SpaceBetween from "@cloudscape-design/components/space-between"
import { useEffect, useRef, useState } from "react"
import { auditApiService, AuditApiError, type CreateAuditReportRequest } from "@/services/auditApi"

const EVENT_TYPES = [
  { value: "WORKFLOW_TRANSITION", label: "Workflow Transition" },
  { value: "DATA_MODIFICATION", label: "Data Modification" },
  { value: "VENDOR_COLLABORATION", label: "Vendor Collaboration" },
  { value: "FILE_UPLOAD", label: "File Upload" },
] as const

interface AuditReportModalProps {
  /**
   * Whether the modal is visible.
   */
  open: boolean
  /**
   * Callback to close the modal.
   */
  onClose: () => void
  /**
   * Studies the caller can see, offered as report filters.
   */
  studies: Array<{ studyId: string; name: string }>
  /**
   * Callback on successful report creation — receives the reportId for polling.
   */
  onSuccess?: (reportId: string) => void
}

export default function AuditReportModal({
  open,
  onClose,
  studies,
  onSuccess,
}: AuditReportModalProps) {
  const [startDate, setStartDate] = useState("")
  const [endDate, setEndDate] = useState("")
  const [selectedEventTypes, setSelectedEventTypes] = useState<Set<string>>(new Set())
  const [selectedStudyIds, setSelectedStudyIds] = useState<string[]>([])
  const [format, setFormat] = useState<"csv" | "html">("csv")
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [dateError, setDateError] = useState<string | null>(null)

  // Idempotency key — same key on retry (safe dedup), regenerated after success
  const idempotencyKeyRef = useRef(crypto.randomUUID())

  // Reset all form state when the modal is opened
  const prevOpenRef = useRef(open)
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setStartDate("")
      setEndDate("")
      setSelectedEventTypes(new Set())
      setSelectedStudyIds([])
      setFormat("csv")
      setSubmitting(false)
      setError(null)
      setDateError(null)
    }
    prevOpenRef.current = open
  }, [open])

  const validateDates = (): boolean => {
    const hasStart = startDate.length > 0
    const hasEnd = endDate.length > 0

    if (hasStart !== hasEnd) {
      setDateError("Provide both start and end dates, or leave both empty for all time.")
      return false
    }

    if (hasStart && hasEnd && startDate > endDate) {
      setDateError("Start date must be before end date.")
      return false
    }

    setDateError(null)
    return true
  }

  const handleSubmit = async () => {
    setError(null)

    if (!validateDates()) return

    setSubmitting(true)

    const request: CreateAuditReportRequest = {
      filters: {
        ...(startDate && endDate
          ? { dateRange: { start: `${startDate}T00:00:00Z`, end: `${endDate}T23:59:59Z` } }
          : {}),
        ...(selectedEventTypes.size > 0 ? { eventTypes: [...selectedEventTypes] } : {}),
        ...(selectedStudyIds.length > 0 ? { studyIds: selectedStudyIds } : {}),
      },
      format,
    }

    try {
      const result = await auditApiService.createReport(request, idempotencyKeyRef.current)
      idempotencyKeyRef.current = crypto.randomUUID()
      onSuccess?.(result.reportId)
      onClose()
    } catch (err) {
      if (err instanceof AuditApiError) {
        setError(err.message)
      } else {
        setError(err instanceof Error ? err.message : "An unexpected error occurred")
      }
    } finally {
      setSubmitting(false)
    }
  }

  const toggleEventType = (value: string, checked: boolean) => {
    setSelectedEventTypes((prev) => {
      const next = new Set(prev)
      if (checked) next.add(value)
      else next.delete(value)
      return next
    })
  }

  const studyOptions: MultiselectProps.Option[] = studies.map((s) => ({
    value: s.studyId,
    label: s.name,
    description: s.studyId,
  }))

  const selectedStudyOptions: MultiselectProps.Option[] = selectedStudyIds.map((id) => {
    const study = studies.find((s) => s.studyId === id)
    return { value: id, label: study?.name ?? id }
  })

  if (!open) return null

  return (
    <Modal
      visible={open}
      onDismiss={onClose}
      size="medium"
      header="Generate Audit Report"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" onClick={() => void handleSubmit()} loading={submitting}>
              {submitting ? "Generating..." : "Generate Report"}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <Box variant="p" color="text-body-secondary">
          Generate a compliance report from audit events. The report will be created in the
          background — you can download it from the reports list once complete.
        </Box>

        <SpaceBetween size="s" direction="horizontal">
          <FormField label="Start date">
            <DatePicker
              value={startDate}
              onChange={({ detail }) => {
                setStartDate(detail.value)
                setDateError(null)
              }}
              placeholder="YYYY/MM/DD"
              openCalendarAriaLabel={(selectedDate) =>
                selectedDate
                  ? `Choose start date, selected date is ${selectedDate}`
                  : "Choose start date"
              }
            />
          </FormField>
          <FormField label="End date">
            <DatePicker
              value={endDate}
              onChange={({ detail }) => {
                setEndDate(detail.value)
                setDateError(null)
              }}
              placeholder="YYYY/MM/DD"
              openCalendarAriaLabel={(selectedDate) =>
                selectedDate
                  ? `Choose end date, selected date is ${selectedDate}`
                  : "Choose end date"
              }
            />
          </FormField>
        </SpaceBetween>

        {dateError && <Alert type="error">{dateError}</Alert>}

        <FormField label="Event types" description="Leave empty to include all event types">
          <SpaceBetween size="xs">
            {EVENT_TYPES.map((et) => (
              <Checkbox
                key={et.value}
                checked={selectedEventTypes.has(et.value)}
                onChange={({ detail }) => toggleEventType(et.value, detail.checked)}
              >
                {et.label}
              </Checkbox>
            ))}
          </SpaceBetween>
        </FormField>

        <FormField label="Studies" description="Leave empty to include all studies">
          <Multiselect
            selectedOptions={selectedStudyOptions}
            onChange={({ detail }) =>
              setSelectedStudyIds(
                detail.selectedOptions.map((o) => o.value).filter((v): v is string => !!v),
              )
            }
            options={studyOptions}
            placeholder="Select studies"
            filteringType="auto"
          />
        </FormField>

        <FormField label="Format">
          <RadioGroup
            value={format}
            onChange={({ detail }) => setFormat(detail.value as "csv" | "html")}
            items={[
              { value: "csv", label: "CSV", description: "Tabular data for analysis or export" },
              { value: "html", label: "HTML", description: "Human-readable compliance document" },
            ]}
          />
        </FormField>

        {error && <Alert type="error">{error}</Alert>}
      </SpaceBetween>
    </Modal>
  )
}
